import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useGetFaultDetailsQuery,
  useUpdateFaultMutation,
} from "../../../api/faultsApi";
import { useCreateTechnicianLogMutation } from "../../../api/technicianLogApi";
import { UploadFilesSection } from "../../../components/FileUploadSection";
import useAuth from "../../../hooks/useAuth";
import { useUploadTechnicianLogPhotos } from "../../../hooks/useUploadTechnicianLogPhoto";
import { useListTechniciansQuery } from "../../../api/managementApi";
import { useTranslation } from "react-i18next";
import { namespaces } from "../../../consts/i18n";
import { STATUS_OPTIONS } from "../../../consts/panelFilterKeys";

const MOST_COMMON_EVENTS = [
  { id: "1", translationKey: "sidebar.panels.faultEvents.check" },
  { id: "2", translationKey: "sidebar.panels.faultEvents.service" },
  { id: "3", translationKey: "sidebar.panels.faultEvents.panelChange" },
  { id: "4", translationKey: "sidebar.panels.faultEvents.diodeChange" },
  { id: "5", translationKey: "sidebar.panels.faultEvents.wiringChange" },
  { id: "6", translationKey: "sidebar.panels.faultEvents.cleaning" },
  { id: "7", translationKey: "sidebar.panels.faultEvents.diagnostics" },
  { id: "8", translationKey: "sidebar.panels.faultEvents.other" },
];

export const UpdateFault = () => {
  const { t } = useTranslation(namespaces.viewer);
  const navigate = useNavigate();
  const storeTechnicianName = useSelector((state) => state.technician.name);
  const { projectId, clientId, stateId } = useParams();
  const [searchParams] = useSearchParams();
  const panelId = searchParams.get("panelId");
  const faultId = searchParams.get("faultId");

  const { data: technicians } = useListTechniciansQuery({ projectId });
  const { isTechnician } = useAuth();

  const [technicianName, setTechnicianName] = useState(
    storeTechnicianName || "",
  );
  const [action, setAction] = useState("");
  const [otherAction, setOtherAction] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("");
  const [files, setFiles] = useState([]);

  const { data: fault, isLoading: isLoadingFault } = useGetFaultDetailsQuery({
    faultId,
  });
  const [updateFault, { isLoading: isUpdatingFault }] =
    useUpdateFaultMutation();
  const [createTechnicianLog, { isLoading: isCreatingTechnicianLog }] =
    useCreateTechnicianLogMutation();
  const { uploadTechnicianLogPhotos, isUploading } =
    useUploadTechnicianLogPhotos();

  /**
   * @param {import("react").FormEvent} e
   */
  async function handleSubmit(e) {
    e.preventDefault();

    try {
      await updateFault({
        faultId,
        action,
        otherAction,
        note,
        status,
      });

      const log = await createTechnicianLog({
        faultId,
        technicianName,
        action,
        note,
        date: new Date().toISOString(),
      }).unwrap();

      const logId = log.data.id;

      if (logId && files.length) {
        for (const file of files) {
          const res = await uploadTechnicianLogPhotos(logId, [file]);
          if (!res) {
            // TODO: 🚨 trigger alert saying "failed to upload photos"
            // TODO: 🚨 revert mutations on BE to original data

            throw new Error("File upload failed");
          }
        }
      }

      setFiles([]);

      navigate(
        `/clients/${clientId}/projects/${projectId}/panels/${stateId}/panel-overview?panelId=${panelId}`,
      );
    } catch (err) {
      console.error(err);
    }
  }

  if (isLoadingFault) {
    return <p>Loading...</p>;
  }

  return (
    <section className="h-full bg-gray-100">
      <div className="p-8 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-center">
          {t("sidebar.panels.faultRecord")}
        </h1>
        <form className="mt-4 text-zinc-500" onSubmit={handleSubmit}>
          <div className="flex gap-8 items-center w-full">
            <label className="flex flex-col gap-1">
              <span className="font-bold text-xs">{t("common.date")}</span>
              <input
                className="mt-1 py-2 px-4 text-sm rounded-lg shadow-sm bg-white"
                type="date"
                name="date"
                value={new Date().toISOString().split("T")[0]}
                readOnly
              />
            </label>
            <label className="flex flex-col gap-1 grow">
              <span className="font-bold text-xs">
                {t("common.serviceTechnician")}
              </span>
              {(isTechnician && storeTechnicianName) ||
              (!isTechnician && !technicians?.length) ? (
                <input
                  className="mt-1 py-2 px-4 text-sm rounded-lg shadow-sm"
                  type="text"
                  name="technician"
                  value={technicianName}
                  disabled={isTechnician}
                  onChange={(e) => setTechnicianName(e.target.value)}
                />
              ) : (
                <select
                  name="technician"
                  className="mt-1 py-2 px-4 text-sm rounded-lg shadow-sm"
                  onChange={(e) => setTechnicianName(e.target.value)}
                  defaultValue=""
                >
                  {!technicianName && (
                    <option key="default" disabled value="">
                      {" "}
                      -- {t("common.choose")} --{" "}
                    </option>
                  )}
                  {technicians &&
                    technicians.map((technician) => (
                      <option key={technician.id} value={technician.name}>
                        {technician.name}
                      </option>
                    ))}
                </select>
              )}
            </label>
            <label className="flex flex-col gap-1 grow">
              <span className="font-bold text-xs">
                {t("sidebar.panels.faultType")}
              </span>
              <input
                className="mt-1 py-2 px-4 text-sm rounded-lg shadow-sm bg-white cursor-not-allowed"
                type="text"
                name="faultType"
                value={fault.name}
                readOnly
                disabled
              />
            </label>
          </div>

          <section className="flex flex-row mt-8 gap-4">
            {/* Most Frequent Events */}
            <div className="w-2/3">
              <div className="flex flex-col gap-1">
                <span className="text-primary font-bold text-xs">
                  {t("sidebar.panels.commonService")}
                </span>
                <div className="flex flex-wrap gap-4">
                  {MOST_COMMON_EVENTS.map((event) => (
                    <label
                      key={event.id}
                      className="has-[:checked]:bg-primary has-[:checked]:text-white cursor-pointer bg-white flex items-center font-bold text-slate-800 py-2 px-5 text-sm rounded-lg shadow-sm"
                    >
                      <>
                        <input
                          key={event.id}
                          className="appearance-none"
                          type="radio"
                          name="action"
                          required
                          value={t(event.translationKey)}
                          onChange={(e) => setAction(e.target.value)}
                          checked={action === t(event.translationKey)}
                        />
                        <span>{t(event.translationKey)}</span>
                      </>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Another Event */}
            <div className="w-1/3">
              <label className="flex flex-col gap-1 grow">
                <span className="font-bold text-xs text-primary">
                  {t("sidebar.panels.otherService")}
                </span>
                <input
                  className="text-slate-800 font-bold py-4 text-center px-4 text-sm rounded-lg shadow-sm bg-white"
                  type="text"
                  name="otherAction"
                  onChange={(e) => {
                    if (action !== "Jiná akce") {
                      setAction("Jiná akce");
                    }

                    setOtherAction(e.target.value);

                    if (e.target.value === "") {
                      setAction("");
                    }
                  }}
                  required={action === "Jiná akce"}
                  placeholder={t("sidebar.panels.attachFault")}
                />
              </label>
            </div>
          </section>

          {/* File */}
          <UploadFilesSection files={files} onFilesChange={setFiles} />

          {/* Most Frequent Events */}
          <section className="flex flex-row mt-16 gap-4">
            <div className="max-w-fit">
              <div className="flex flex-col gap-1">
                <span className="text-primary font-bold text-xs">
                  {t("sidebar.panels.faultStatus")}
                </span>
                <div className="py-2 px-4 flex flex-wrap gap-6 bg-white rounded-full">
                  {STATUS_OPTIONS.map((statusOption, i) => (
                    <label
                      key={i}
                      className={`${
                        statusOption.key === "SOLVED"
                          ? "has-[:checked]:bg-green-700"
                          : statusOption.key === "OBSERVATION"
                            ? "has-[:checked]:bg-violet-600"
                            : "has-[:checked]:bg-red-700"
                      } has-[:checked]:text-white cursor-pointer bg-secondary font-bold flex items-center text-zinc-400 py-2 px-5 text-sm rounded-lg shadow-sm`}
                    >
                      <input
                        className="appearance-none"
                        type="radio"
                        name="status"
                        value={statusOption.key}
                        onChange={() => setStatus(statusOption.key)}
                        required
                      />
                      <span>{t(statusOption.translationKey)}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <label className="flex flex-col gap-1 grow">
              <span className="text-primary font-bold text-xs">
                {t("form.note")}
              </span>
              <textarea
                className="py-4 px-4 text-sm rounded-lg"
                name="note"
                rows={1}
                placeholder={t("form.note")}
                maxLength={50}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
          </section>

          {/* Buttons */}
          <div className="mt-16 flex gap-6 w-full justify-center">
            <button
              className="py-4 px-12 bg-white text-black font-bold text-xl rounded-lg shadow-sm"
              onClick={() =>
                navigate(
                  `/clients/${clientId}/projects/${projectId}/panels/${stateId}/panel-overview?panelId=${panelId}`,
                )
              }
            >
              {t("common.throwAway")}
            </button>
            <button
              className="has-[:disabled]:cursor-not-allowed has-[:disabled]:bg-zinc-300 py-4 px-12 bg-primary text-white font-bold text-xl rounded-lg shadow-sm"
              type="submit"
              disabled={
                isUpdatingFault || isCreatingTechnicianLog || isUploading
              }
            >
              {t("common.create")}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};
